import React, { useMemo, useRef } from 'react';

import EasyPayOption from '../easy-pay-option/easy-pay-option';

import { DTCC_PROMOTION_PLANS } from '../../shared/graphql/dtcc-queries';

import {
  useDTCCApplication,
  useMessage,
  useModal,
  useMyStore,
  useNavigateTo,
  useQuery
} from '../../shared/hooks/hooks';

import { trackAction } from '../../shared/utils/analytics/analytics';
import { interpolateCmsData } from '../../shared/utils/cms/cms';
import { getIsPromotional } from '../../shared/utils/dtcc-banner/dtcc-banner';
import { convertIntPlanDuration } from '../../shared/utils/payment-utils/payment-utils';

import {
  CarCareOnePromotionData,
  Maybe
} from '../../shared/types/graphql-types';
import { MapPrice } from '../../shared/types/product';
import { DTCC_APPLICATION_STATUS } from '../../shared/constants/cart';
import {
  IMAGE_KEYS,
  MONTHLY_MESSAGING_KEYS,
  PROMOTIONAL_MESSAGING_KEYS
} from '../../shared/constants/dtcc-banner-constants';
import { ONE_CLICK_MODAL } from '../../shared/constants/modals';
import { PageTypes } from '../../shared/constants/page-types';

interface DTCCBannerProps {
  applyNowButtonId: string;
  className?: string;
  combinedMapPrice?: MapPrice;
  formattedValue?: Maybe<string>;
  linkText?: string;
  pageType?: keyof typeof PageTypes;
  setShowPromoTitle?: (show) => void;
  shouldOpenLinkInNewTab?: boolean;
  showImageAfterMessage?: boolean;
  showLinkAsCTA?: boolean;
  value?: number;
}

function getPromoInPriceRange(
  price: number,
  promotions: Maybe<CarCareOnePromotionData>[]
) {
  return promotions.find(
    p => p?.minOrderAmount <= price && p?.maxOrderAmount >= price
  );
}

function getMonthlyPayment(cost, planDuration) {
  return Math.ceil((cost || 0) / (planDuration || 0));
}

function DTCCBanner(props: DTCCBannerProps) {
  const {
    applyNowButtonId,
    className = '',
    combinedMapPrice = null,
    formattedValue,
    linkText: linkTextProp = 'learn how',
    pageType,
    setShowPromoTitle,
    shouldOpenLinkInNewTab = false,
    showImageAfterMessage,
    showLinkAsCTA,
    value
  } = props;
  const { myStore } = useMyStore();
  const storeCode = myStore?.code;
  const { openModal } = useModal();
  const { dtccApplicationStatus, isClientLoaded } =
    useDTCCApplication(pageType);
  const navigateTo = useNavigateTo();
  const promoDataRef = useRef<Maybe<CarCareOnePromotionData> | undefined>();

  const valueAsNumber = typeof value === 'number' ? value : NaN;
  const { data: dtccPromoData } = useQuery(DTCC_PROMOTION_PLANS, {
    skip: isNaN(valueAsNumber),
    variables: {
      storeCode
    }
  });

  const planData = useMemo(
    () =>
      getPromoInPriceRange(
        valueAsNumber,
        dtccPromoData?.storeCheckout?.['allCC1PromotionPlans'] || []
      ),
    [dtccPromoData, valueAsNumber]
  );

  const planDuration = convertIntPlanDuration(planData?.planDuration);
  const monthlyPayment = getMonthlyPayment(value, planDuration);
  const isPromotional = getIsPromotional(combinedMapPrice, planDuration);
  const textContentKeys = isPromotional
    ? PROMOTIONAL_MESSAGING_KEYS
    : MONTHLY_MESSAGING_KEYS;
  const imageContentKeys = window.isDT ? IMAGE_KEYS.dt : IMAGE_KEYS.at;
  const messages = useMessage(textContentKeys.concat(imageContentKeys)) || {};
  const linkURL = messages.DTCC_PROMO_MORE_DETAILS_LINK_URL;

  if (
    !isClientLoaded ||
    (!planData && !isPromotional) ||
    dtccApplicationStatus === DTCC_APPLICATION_STATUS.INSUFFICIENT
  ) {
    return null;
  }

  function handleOnClick() {
    trackAction(
      isPromotional
        ? 'dtcc_cta_apply_promotional_learnmore'
        : 'dtcc_cta_apply_monthly_learnhow'
    );
    if (isPromotional) {
      !shouldOpenLinkInNewTab
        ? navigateTo(linkURL)
        : window.open(linkURL, '_blank');
    } else {
      openModal(ONE_CLICK_MODAL, {
        applyNowButtonId,
        formattedValue,
        monthlyPayment,
        pageType,
        planDuration,
        termsAndConditions:
          planData?.oneClickModalTerms ||
          promoDataRef.current?.oneClickModalTerms
      });
    }
  }

  const imageAltText =
    messages.DTCC_PROMO_IMAGE_DISCOUNTTIRE_ALTTEXT ??
    messages.DTCC_PROMO_IMAGE_AMERICASTIRE_ALTTEXT;
  const imageURL =
    messages.DTCC_PROMO_IMAGE_DISCOUNTTIRE_URL ??
    messages.DTCC_PROMO_IMAGE_AMERICASTIRE_URL;
  const linkText = combinedMapPrice
    ? messages.DTCC_PROMO_MODAL_TRIGGER_MESSAGE ??
      messages.DTCC_PROMO_MORE_DETAILS_LINK_MESSAGE
    : linkTextProp;

  function getPromotionalText() {
    let promotionalText;

    if (messages.DTCC_PROMO_FINANCING_DETAILS) {
      promotionalText = interpolateCmsData(
        {
          financingPeriod: planDuration,
          monthlyPrice: monthlyPayment
        },
        messages.DTCC_PROMO_FINANCING_DETAILS
      );
    } else {
      promotionalText = messages.DTCC_PROMO_FINANCING_AVAILABLE;
      setShowPromoTitle && setShowPromoTitle(false);
    }

    return promotionalText;
  }

  return (
    <EasyPayOption
      className={`dtcc-apply-now ${className} ${
        isPromotional ? 'dtcc-apply-now--promotional' : ''
      }`}
      imageAlt={imageAltText}
      imageUrl={imageURL}
      linkText={linkText}
      message={
        <span
          dangerouslySetInnerHTML={{
            __html: getPromotionalText()
          }}
        />
      }
      onClick={handleOnClick}
      showImageAfterMessage={showImageAfterMessage}
      showLinkAsCTA={showLinkAsCTA}
    />
  );
}

export default DTCCBanner;
